import { useMutation } from '@apollo/client';
import type { PackageCreateParams } from '@openx/types/gqlTypes.ts';
import type { UsePackageCreate } from '@openx/types/package/PackageHookTypes.ts';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CreatePackageDocument,
  type CreatePackageMutationVariables,
} from '../../../graphql/mutation/packages/CreatePackage.generated.tsx';
import { NestedErrorCode } from '../../../modules/graphql/types.ts';
import { useLocalErrorHandling } from '../../../modules/graphql/useLocalErrorHandling.ts';
import { transformTargetingForMutation } from './transformTargetingForMutation.ts';

export const usePackageCreate: UsePackageCreate = () => {
  const [createPackageMutation, { loading: isCreating }] = useMutation(CreatePackageDocument, {
    refetchQueries: [],
  });
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const errorCallback = useCallback(
    error => {
      if (error.code === NestedErrorCode.VALIDATION_ERROR && error.message) {
        enqueueSnackbar(`Package creation failed. ${error.message}`, { variant: 'error' });
        return true;
      }

      return false;
    },
    [enqueueSnackbar]
  );

  useLocalErrorHandling(CreatePackageDocument, errorCallback);

  const createPackage = useCallback(
    async (pckg: Partial<PackageCreateParams>) => {
      const urls = pckg.urls ? (pckg.urls as string[]) : undefined;

      const packageCreateVariables: CreatePackageMutationVariables = {
        package: { ...pckg, urls, targeting: transformTargetingForMutation(pckg.targeting) },
      };

      if (packageCreateVariables.package.private_market) {
        packageCreateVariables.package.private_market.discoverable = packageCreateVariables.package.private_market
          .discoverable
          ? '1'
          : '0';
      }

      try {
        const result = await createPackageMutation({ variables: packageCreateVariables });
        if (!result.data) {
          throw new Error(t('Something went wrong creating package!'));
        }

        enqueueSnackbar(
          t('Package "{packageName}" was successfully created', {
            packageName: result.data.createPackage.name,
          }),
          { variant: 'success' }
        );

        return result.data?.createPackage;
      } catch (e) {
        throw new Error(e);
      }
    },
    [createPackageMutation, enqueueSnackbar, t]
  );

  return { createPackage, isCreating };
};
